import { RecordFormFrame } from '@/app/work-tabs/record-form-frame';
import { useWorkTab, useWorkFieldId } from '@/app/work-tabs/work-tab-context';
import { CustomerCategorySelect, DeliveryTermSelect } from '@/features/settings';
import { dirtyFieldsPatch } from '@/lib/form-patch';
import { PriceGroupSelect } from '@/features/settings';
import { CountrySelect } from '@/components/common/country-select';
import { LegalFormSelect } from '@/components/common/legal-form-select';
import { IndustryCodeSelect } from './industry-code-select';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CUSTOMER_INDUSTRY_CODE_MAX_LENGTH,
  DOCUMENT_LANGUAGES,
  FRENCH_IDENTIFIER_INPUT_MAX_LENGTH,
  INCOTERMS,
  PERMISSIONS,
  LEAD_SOURCES,
  LEAD_STATUSES,
  SUPPLIER_RATINGS,
  frenchCompanyIdentifierConsistencySchema,
  partnerCreateSchema,
  partnerUpdateSchema,
  partnerWriteSchema,
  type CustomFieldPartnerRole,
  type Partner,
  type PartnerCreateDefaults,
  type PartnerCreateInput,
  type PartnerUpdateInput,
} from '@erp/shared';
import { AlertCircle, ChevronDown, Loader2, WandSparkles } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm, type FieldNamesMarkedBoolean, type UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FormField } from '@/components/common/form-field';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { SheetFooter } from '@/components/ui/sheet';
import {
  CategorizedCustomFields,
  CustomFieldLoadState,
  useMasterCustomFields,
} from '@/features/custom-fields';
import { AddressResolutionAssist, SubdivisionSelect } from '@/features/address-resolution';
import { InlineHelp } from '@/features/help';
import { usePermissions } from '@/features/auth';
import { PaymentTermSelect } from '@/features/payment-terms';
import {
  useCreatePartner,
  usePartnerCreateDefaults,
  useUpdatePartner,
} from '@/features/partners/api/use-partners';
import type { ApiError } from '@/lib/api-client';
import { toastApiError } from '@/lib/api-error-toast';

const PARTNER_TYPES = ['COMPANY', 'PERSON'] as const;
const PARTNER_STATUSES = ['ACTIVE', 'PROSPECT', 'BLOCKED', 'INACTIVE'] as const;
const TAX_CATEGORIES = [
  'DOMESTIC',
  'EU_B2B',
  'EU_B2C',
  'THIRD_COUNTRY',
  'REVERSE_CHARGE_DOMESTIC',
  'TAX_EXEMPT',
] as const;

const NONE = '__none__';
const ERROR_SUMMARY = '__error_summary__';

const emptyToNull = {
  setValueAs: (value: unknown) =>
    String(value ?? '').trim() === '' ? null : String(value ?? '').trim(),
};
const emptyToUndefined = {
  setValueAs: (value: unknown) =>
    String(value ?? '').trim() === '' ? undefined : String(value ?? '').trim(),
};
const numberOrUndefined = {
  setValueAs: (value: unknown) =>
    value === null || value === undefined || String(value).trim() === ''
      ? undefined
      : Number(value),
};
const numberOrNull = {
  setValueAs: (value: unknown) =>
    value === null || value === undefined || String(value).trim() === '' ? null : Number(value),
};

type FormValues = PartnerUpdateInput & { creationMode?: 'QUICK' | 'COMPLETE' };

export type PartnerFormPreset = 'customer' | 'prospect' | 'supplier' | 'all';

function presetRoles(
  preset: PartnerFormPreset,
): Pick<FormValues, 'isCustomer' | 'isProspect' | 'isSupplier'> {
  return {
    isCustomer: preset === 'customer' || preset === 'all',
    isProspect: preset === 'prospect',
    isSupplier: preset === 'supplier',
  };
}

function toFormValues(
  partner: Partner | null,
  preset: PartnerFormPreset,
  defaults?: PartnerCreateDefaults,
  duplicateFrom: Partner | null = null,
): FormValues {
  if (partner === null && duplicateFrom !== null) {
    return {
      ...toFormValues(duplicateFrom, preset, defaults),
      partnerNumber: null,
      creationMode: 'COMPLETE',
      status: duplicateFrom.isProspect && !duplicateFrom.isCustomer ? 'PROSPECT' : 'ACTIVE',
      geocodingPublic: false,
      debtorAccountNumber: null,
      creditorAccountNumber: null,
      creditLimit: null,
      creditRating: null,
      supplierIban: null,
      supplierBic: null,
      supplierBankName: null,
      supplierRating: null,
      leadStatus: duplicateFrom.isProspect ? 'NEW' : null,
      leadScore: null,
      dealSizeEstimated: null,
      winProbability: null,
      expectedClosingDate: null,
      lostReason: null,
    };
  }
  if (partner === null) {
    return {
      creationMode: 'QUICK',
      type: 'COMPANY',
      status: preset === 'prospect' ? 'PROSPECT' : 'ACTIVE',
      ...presetRoles(preset),
      countryCode: defaults?.countryCode ?? 'DE',
      currency: defaults?.currency ?? 'EUR',
      preferredLanguage: defaults?.preferredLanguage ?? 'de',
      taxCategory: defaults?.taxCategory ?? 'DOMESTIC',
      paymentTermsDays: defaults?.paymentTermsDays ?? 14,
      discountPercent: defaults?.discountPercent ?? '0',
      discountDays: defaults?.discountDays ?? 0,
      defaultDiscountPct: '0',
      paymentTermId: defaults?.paymentTermId ?? null,
      deliveryBlock: false,
      dunningBlock: false,
      leadStatus: preset === 'prospect' ? 'NEW' : null,
      tags: [],
      customFields: {},
    };
  }

  return {
    creationMode: 'COMPLETE',
    partnerNumber: partner.partnerNumber,
    type: partner.type,
    status: partner.status,
    isCustomer: partner.isCustomer,
    isProspect: partner.isProspect,
    isSupplier: partner.isSupplier,
    companyName: partner.companyName,
    tradingName: partner.tradingName,
    legalForm: partner.legalForm,
    legalFormCode: partner.legalFormCode,
    legalFormName: partner.legalFormName,
    legalFormJurisdiction: partner.legalFormJurisdiction,
    department: partner.department,
    firstName: partner.firstName,
    lastName: partner.lastName,
    email: partner.email,
    phone: partner.phone,
    mobile: partner.mobile,
    website: partner.website,
    street: partner.street,
    geocodingPublic: partner.geocodingPublic,
    houseNumber: partner.houseNumber,
    addressLine2: partner.addressLine2,
    postalCode: partner.postalCode,
    city: partner.city,
    region: partner.region,
    subdivisionCode: partner.subdivisionCode ?? null,
    subdivisionName: partner.subdivisionName ?? null,
    regionOrigin: partner.regionOrigin ?? null,
    countryCode: partner.countryCode,
    vatId: partner.vatId,
    taxNumber: partner.taxNumber,
    siren: partner.siren ?? null,
    siret: partner.siret ?? null,
    gln: partner.gln,
    eoriNumber: partner.eoriNumber,
    taxCategory: partner.taxCategory,
    preferredLanguage: partner.preferredLanguage,
    currency: partner.currency,
    industry: partner.industry,
    industryCode: partner.industryCode,
    ownerUserId: partner.ownerUserId,
    tags: partner.tags,
    customFields: partner.customFields,
    debtorAccountNumber: partner.debtorAccountNumber,
    creditLimit: partner.creditLimit,
    creditRating: partner.creditRating,
    priceGroupId: partner.priceGroupId,
    priceGroup: partner.priceGroup,
    defaultDiscountPct: partner.defaultDiscountPct,
    paymentTermId: partner.paymentTermId,
    paymentTermsDays: partner.paymentTermsDays,
    discountDays: partner.discountDays,
    discountPercent: partner.discountPercent,
    deliveryTerms: partner.deliveryTerms,
    deliveryBlock: partner.deliveryBlock,
    dunningBlock: partner.dunningBlock,
    minRemainingShelfLifeDays: partner.minRemainingShelfLifeDays,
    leadStatus: partner.leadStatus,
    leadSource: partner.leadSource,
    leadScore: partner.leadScore,
    dealSizeEstimated: partner.dealSizeEstimated,
    winProbability: partner.winProbability,
    expectedClosingDate: partner.expectedClosingDate,
    lostReason: partner.lostReason,
    creditorAccountNumber: partner.creditorAccountNumber,
    supplierRating: partner.supplierRating,
    minimumOrderValue: partner.minimumOrderValue,
    deliveryTimeDays: partner.deliveryTimeDays,
    incoterms: partner.incoterms,
    supplierIban: partner.supplierIban,
    supplierBic: partner.supplierBic,
    supplierBankName: partner.supplierBankName,
  };
}

function PartnerFormSection({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <details className="group/partner-section rounded-md border border-border p-4">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {title}
        <ChevronDown
          className="h-4 w-4 shrink-0 group-open/partner-section:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className={className ?? 'grid gap-4 pt-4 sm:grid-cols-2'}>{children}</div>
    </details>
  );
}

export interface PartnerFormSheetProps {
  inline?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner: Partner | null;
  duplicateFrom?: Partner | null;
  preset?: PartnerFormPreset;
  onSaved?: (partner: Partner) => void;
}

export function PartnerFormSheet(props: PartnerFormSheetProps): JSX.Element {
  return <EditablePartnerFormSheet {...props} />;
}

function EditablePartnerFormSheet({
  inline = false,
  open,
  onOpenChange,
  partner,
  duplicateFrom = null,
  preset = 'all',
  onSaved,
}: PartnerFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const domId = useWorkFieldId();
  const { can } = usePermissions();
  const canEditPricing = can(PERMISSIONS.CUSTOMER_PRICING_WRITE);
  const createPartner = useCreatePartner();
  const createDefaults = usePartnerCreateDefaults();
  const updatePartner = useUpdatePartner(partner?.id ?? '');
  const isEdit = partner !== null;
  const isDuplicate = !isEdit && duplicateFrom !== null;
  const [quickMode, setQuickMode] = useState(!isEdit && !isDuplicate);
  const [customPaymentOpen, setCustomPaymentOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const initializedKey = useRef<string | null>(null);
  const allowSavedNavigation = useRef(false);
  const appliedDefaults = useRef<PartnerCreateDefaults | undefined>(undefined);
  const [focusField, setFocusField] = useState<string | null>(null);

  const editedFieldsRef = useRef(new Set<string>());
  const dirtyFieldsRef = useRef<Partial<Readonly<FieldNamesMarkedBoolean<FormValues>>>>({});

  const form = useForm<FormValues>({
    resolver: async (values, context, options) => {
      if (!isEdit) return zodResolver(partnerCreateSchema)(values, context, options);
      const patch = dirtyFieldsPatch(values, dirtyFieldsRef.current, editedFieldsRef.current);
      if (
        values.paymentTermId !== null &&
        values.paymentTermId !== undefined &&
        (patch.paymentTermsDays !== undefined ||
          patch.discountPercent !== undefined ||
          patch.discountDays !== undefined)
      ) {
        // Reselecting the same rule must not turn its changed values into an individual agreement.
        patch.paymentTermId = values.paymentTermId;
      }
      const validation = await zodResolver(partnerUpdateSchema)(patch, context, options);
      if (!('siren' in patch) && !('siret' in patch)) return validation;
      // Compare a changed identifier with its saved counterpart without adding it to the patch.
      const identifiers = frenchCompanyIdentifierConsistencySchema.safeParse({
        siren: values.siren,
        siret: values.siret,
      });
      if (identifiers.success) return validation;
      const errors = { ...validation.errors };
      for (const issue of identifiers.error.issues) {
        const field = issue.path[0];
        if (field === 'siren' || field === 'siret') {
          errors[field] = { type: issue.code, message: issue.message };
        }
      }
      return { values: {}, errors };
    },
    shouldFocusError: false,
    defaultValues: toFormValues(partner, preset, createDefaults.data, duplicateFrom),
  });
  dirtyFieldsRef.current = form.formState.dirtyFields;
  const setValue = useCallback<UseFormSetValue<FormValues>>(
    (name, value, options) => {
      if (options?.shouldDirty) editedFieldsRef.current.add(name.split('.')[0] ?? name);
      form.setValue(name, value, options);
    },
    [form],
  );
  const [customFieldErrors, setCustomFieldErrors] = useState<Readonly<Record<string, string>>>({});

  useEffect(() => {
    if (!open) {
      initializedKey.current = null;
      return;
    }
    const key =
      partner?.id ?? (duplicateFrom === null ? `new-${preset}` : `copy-${duplicateFrom.id}`);
    if (initializedKey.current !== key) {
      allowSavedNavigation.current = false;
      editedFieldsRef.current.clear();
      form.reset(toFormValues(partner, preset, createDefaults.data, duplicateFrom));
      initializedKey.current = key;
      appliedDefaults.current = createDefaults.data;
      setQuickMode(partner === null && duplicateFrom === null);
      setCustomPaymentOpen(false);
      setSaveError(null);
      setCustomFieldErrors({});
      setFocusField(null);
    } else if (
      partner === null &&
      duplicateFrom === null &&
      createDefaults.data !== undefined &&
      appliedDefaults.current !== createDefaults.data
    ) {
      // Keep the original baseline for edited fields so delayed defaults cannot mark drafts clean.
      form.reset(toFormValues(null, preset, createDefaults.data), {
        keepDirtyValues: true,
        keepErrors: true,
      });
      appliedDefaults.current = createDefaults.data;
    }
  }, [createDefaults.data, open, partner, duplicateFrom, preset, form]);

  const type = form.watch('type');
  const paymentTermId = form.watch('paymentTermId');
  const hasSelectedPaymentTerm = paymentTermId !== null && paymentTermId !== undefined;
  const [paymentTermsDays, discountDays, discountPercent] = form.watch([
    'paymentTermsDays',
    'discountDays',
    'discountPercent',
  ]);
  const inconsistentPaymentValues =
    typeof discountDays === 'number' &&
    ((typeof paymentTermsDays === 'number' && discountDays > paymentTermsDays) ||
      (discountDays > 0 && typeof discountPercent === 'string' && Number(discountPercent) === 0));
  const customPaymentLabel = hasSelectedPaymentTerm
    ? t('paymentTerms.savedValues', {
        due:
          typeof paymentTermsDays === 'number'
            ? t('paymentTerms.netDays', { count: paymentTermsDays })
            : t('legacy.unknown'),
        percent: discountPercent ?? t('legacy.unknown'),
        days: discountDays ?? t('legacy.unknown'),
      })
    : t('paymentTerms.customAgreement');
  const customFields = form.watch('customFields') ?? {};
  const tags = form.watch('tags') ?? [];
  const roles = {
    isCustomer: form.watch('isCustomer') === true,
    isProspect: form.watch('isProspect') === true,
    isSupplier: form.watch('isSupplier') === true,
  };
  const partnerRoles: CustomFieldPartnerRole[] = [
    ...(roles.isCustomer ? (['CUSTOMER'] as const) : []),
    ...(roles.isProspect ? (['PROSPECT'] as const) : []),
    ...(roles.isSupplier ? (['SUPPLIER'] as const) : []),
  ];
  const definitions = useMasterCustomFields('CUSTOMER', partner?.id ?? null, partnerRoles);

  useEffect(() => {
    // A delayed customer-default reset must also restore untouched custom-field defaults.
    if (!open || isEdit || definitions.data === undefined) return;
    const current = form.getValues('customFields') ?? {};
    const next = { ...current };
    const applicableKeys = new Set(
      definitions.data.definitions.map((definition) => definition.key),
    );
    let changed = false;
    for (const key of Object.keys(next)) {
      if (!applicableKeys.has(key)) {
        delete next[key];
        changed = true;
      }
    }
    for (const definition of definitions.data.definitions) {
      if (next[definition.key] === undefined && definition.defaultValue !== undefined) {
        next[definition.key] = definition.defaultValue;
        changed = true;
      }
    }
    if (changed) setValue('customFields', next);
  }, [definitions.data, createDefaults.data, form, open, partner, isEdit, setValue]);

  function fieldControl(field: string): HTMLElement | null {
    if (field === ERROR_SUMMARY) return errorSummaryRef.current;
    const named = formRef.current?.elements.namedItem(field);
    if (named instanceof HTMLElement && named.getAttribute('type') !== 'hidden') return named;
    const complete = partner !== null || !quickMode;
    const ids: Record<string, string> = {
      isCustomer: complete ? 'p-role-customer' : 'p-quick-role-customer',
      subdivisionCode: complete ? 'p-subdivision-1' : 'p-subdivision-2',
      subdivisionName: complete ? 'p-subdivision-1' : 'p-subdivision-2',
      legalFormCode: complete ? 'p-legal-form' : 'p-quick-legal-form',
      legalFormName: complete ? 'p-legal-form-other' : 'p-quick-legal-form-other',
      countryCode: complete ? 'p-country' : 'p-quick-country',
      preferredLanguage: complete ? 'p-language' : 'p-quick-language',
      paymentTermId: complete ? 'p-payment-term' : 'p-quick-payment',
      paymentTermsDays: complete
        ? hasSelectedPaymentTerm
          ? 'p-payment-term'
          : 'p-payment-days'
        : 'p-quick-payment',
      discountPercent: complete
        ? hasSelectedPaymentTerm
          ? 'p-payment-term'
          : 'p-discount-pct'
        : 'p-quick-payment',
      discountDays: complete
        ? hasSelectedPaymentTerm
          ? 'p-payment-term'
          : 'p-discount-days'
        : 'p-quick-payment',
      priceGroupId: 'p-price-group',
      industry: 'p-industry',
      deliveryTerms: 'p-delivery-terms',
    };
    return document.getElementById(ids[field] ?? `partner-form-custom-field-${field}`);
  }

  function revealField(field: string): void {
    const control = fieldControl(field);
    if (control === null) {
      if (quickMode) setQuickMode(false);
      setFocusField(field);
      return;
    }
    let parent = control.parentElement;
    while (parent !== null && parent !== formRef.current) {
      if (parent instanceof HTMLDetailsElement) parent.open = true;
      parent = parent.parentElement;
    }
    control.focus({ preventScroll: true });
    control.scrollIntoView({ block: 'nearest' });
    setFocusField(null);
  }

  useEffect(() => {
    if (focusField === null || !open) return;
    const frame = window.requestAnimationFrame(() => revealField(focusField));
    return () => window.cancelAnimationFrame(frame);
  });

  const onError = (error: ApiError): void => {
    setSaveError(error.message);
    setCustomFieldErrors(
      Object.fromEntries(
        (error.validationErrors ?? []).map((issue) => [issue.fieldKey, issue.reason]),
      ),
    );
    for (const issue of error.details ?? []) {
      if (issue.path in partnerWriteSchema.shape) {
        form.setError(issue.path as keyof FormValues, { type: 'server', message: issue.message });
      }
    }
    const firstField = error.details?.find((issue) => issue.path in partnerWriteSchema.shape)?.path;
    if (firstField) setFocusField(firstField);
    else if (error.validationErrors?.length) {
      setQuickMode(false);
    } else setFocusField(ERROR_SUMMARY);
    toastApiError(error, t('partners.saveError'));
  };

  const onSuccess = (saved: Partner): void => {
    allowSavedNavigation.current = true;
    setSaveError(null);
    toast.success(isEdit ? t('partners.updated') : t('partners.created'), {
      description: saved.displayName,
    });
    workTab.update({ dirty: false, busy: false });
    if (!inline || onSaved === undefined) onOpenChange(false);
    onSaved?.(saved);
  };

  const onSubmit = form.handleSubmit(
    (values) => {
      if (createPartner.isPending || updatePartner.isPending) return;
      setSaveError(null);
      setCustomFieldErrors({});
      if (isEdit) {
        updatePartner.mutate(values, { onSuccess, onError });
        return;
      }
      const payload: PartnerCreateInput = partnerCreateSchema.parse({
        ...values,
        creationMode: quickMode ? 'QUICK' : 'COMPLETE',
      });
      if (!canEditPricing) {
        delete payload.priceGroupId;
        delete payload.priceGroup;
        delete payload.defaultDiscountPct;
        delete payload.paymentTermId;
        delete payload.discountPercent;
        delete payload.discountDays;
      }
      createPartner.mutate(payload, { onSuccess, onError });
    },
    (issues) => {
      setSaveError(t('partners.validationSummary'));
      const firstField = Object.keys(issues)[0];
      if (firstField) revealField(firstField);
    },
  );

  const isPending = createPartner.isPending || updatePartner.isPending;
  const workTab = useWorkTab({ dirty: open && form.formState.isDirty, busy: open && isPending });
  const { update: updateWorkTab } = workTab;
  useEffect(() => () => updateWorkTab({ dirty: false, busy: false }), [updateWorkTab]);

  const [discardOpen, setDiscardOpen] = useState(false);
  const dirty =
    open &&
    Object.keys(
      dirtyFieldsPatch(form.getValues(), form.formState.dirtyFields, editedFieldsRef.current),
    ).length > 0;

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent): void => {
      if (allowSavedNavigation.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);
  function requestOpenChange(nextOpen: boolean): void {
    if (!nextOpen && dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(nextOpen);
  }
  function keepEditing(): void {
    setDiscardOpen(false);
  }
  function discard(): void {
    editedFieldsRef.current.clear();
    form.reset(toFormValues(partner, preset, createDefaults.data, duplicateFrom));
    setDiscardOpen(false);
    onOpenChange(false);
  }
  const errors = form.formState.errors;
  const visibleFields = definitions.data?.definitions ?? [];
  const showCompleteForm = isEdit || !quickMode;
  const createTitleKey = {
    all: 'partners.createTitle',
    customer: 'partners.actions.createCustomer',
    prospect: 'partners.actions.createProspect',
    supplier: 'partners.actions.createSupplier',
  }[preset];
  const validationIssues = Object.entries(errors).flatMap(([field, issue]) => {
    if (typeof issue?.message !== 'string') return [];
    const labelKey =
      field === 'isCustomer'
        ? 'partners.sections.roles'
        : field.startsWith('legalForm')
          ? 'partners.fields.legalForm'
          : field.startsWith('subdivision')
            ? 'subdivision.label'
            : `partners.fields.${field}`;
    return [{ field, label: t(labelKey), message: t(issue.message) }];
  });

  function frenchIdentifierFields(prefix: string): JSX.Element {
    return (
      <>
        <div className="flex items-center gap-2 sm:col-span-2">
          <p
            id={domId(`${prefix}-french-identifiers-hint`)}
            className="text-xs text-muted-foreground"
          >
            {t('frenchIdentifiers.hint')}
          </p>
          <span className="inline-flex shrink-0 [&_button]:h-11 [&_button]:w-11">
            <InlineHelp
              articleId="partners.customer-file"
              anchor="french-identifiers"
              label={t('frenchIdentifiers.hint')}
            />
          </span>
        </div>
        {(['siren', 'siret'] as const).map((field) => {
          const id = domId(`${prefix}-${field}`);
          const message = errors[field]?.message;
          return (
            <FormField
              key={field}
              id={id}
              label={t(`partners.fields.${field}`)}
              error={message === undefined ? undefined : t(message)}
            >
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                maxLength={FRENCH_IDENTIFIER_INPUT_MAX_LENGTH}
                className="min-h-11"
                disabled={isPending}
                aria-invalid={message === undefined ? undefined : true}
                aria-describedby={[
                  domId(`${prefix}-french-identifiers-hint`),
                  ...(message === undefined ? [] : [`${id}-error`]),
                ].join(' ')}
                {...form.register(field)}
              />
            </FormField>
          );
        })}
      </>
    );
  }

  return (
    <>
      <RecordFormFrame
        inline={inline}
        open={open}
        onOpenChange={requestOpenChange}
        title={
          isDuplicate
            ? t('duplication.partnerTitle')
            : isEdit
              ? t('partners.editTitle')
              : t(createTitleKey)
        }
        description={t(isDuplicate ? 'duplication.partnerHint' : 'partners.formDescription')}
        dirty={false}
      >
        <form
          onChangeCapture={(event) => {
            if ('name' in event.target && typeof event.target.name === 'string')
              editedFieldsRef.current.add(event.target.name.split('.')[0] ?? event.target.name);
          }}
          ref={formRef}
          onSubmit={onSubmit}
          onKeyDown={(event) => {
            if (
              (event.ctrlKey || event.metaKey) &&
              (event.key.toLowerCase() === 's' || event.key === 'Enter')
            ) {
              event.preventDefault();
              if (!isPending) event.currentTarget.requestSubmit();
            }
          }}
          className="flex min-h-0 flex-1 flex-col gap-4"
          noValidate
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-4 pr-1">
            {!isEdit ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <WandSparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium">
                      {quickMode ? t('partners.quick.title') : t('partners.quick.completeTitle')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {quickMode
                        ? t('partners.quick.description')
                        : t('partners.quick.completeDescription')}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setQuickMode((value) => !value)}
                >
                  {quickMode ? t('partners.quick.showAll') : t('partners.quick.showQuick')}
                </Button>
              </div>
            ) : null}

            {createDefaults.isError && !isEdit ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('partners.quick.defaultsUnavailable')}</AlertDescription>
              </Alert>
            ) : null}

            {saveError === null && Object.keys(errors).length === 0 ? null : (
              <Alert ref={errorSummaryRef} tabIndex={-1} variant="destructive" role="alert">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <AlertTitle>{t('partners.validationTitle')}</AlertTitle>
                  <AlertDescription>
                    {saveError ?? t('partners.validationSummary')}
                  </AlertDescription>
                  {validationIssues.length > 0 ? (
                    <div className="min-w-0">
                      <p className="text-sm">{t('partnerUsability.errorHint')}</p>
                      <ul className="mt-2 space-y-1">
                        {validationIssues.map((issue) => (
                          <li key={issue.field}>
                            <button
                              type="button"
                              className="min-h-11 text-left text-sm underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              onClick={() => revealField(issue.field)}
                            >
                              {issue.label}: {issue.message}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </Alert>
            )}

            {showCompleteForm ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t('partnerUsability.sectionsHint')}
                </p>
                <PartnerFormSection
                  title={t('partners.sections.roles')}
                  className="flex flex-col gap-3 pt-4"
                >
                  <p className="text-xs text-muted-foreground">
                    {t('partners.sections.rolesHint')}
                  </p>
                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={domId('p-role-customer')}
                        checked={roles.isCustomer}
                        onCheckedChange={(checked) =>
                          setValue('isCustomer', checked === true, { shouldDirty: true })
                        }
                      />
                      <Label htmlFor={domId('p-role-customer')} className="font-normal">
                        {t('partners.roles.customer')}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={domId('p-role-prospect')}
                        checked={roles.isProspect}
                        onCheckedChange={(checked) =>
                          setValue('isProspect', checked === true, { shouldDirty: true })
                        }
                      />
                      <Label htmlFor={domId('p-role-prospect')} className="font-normal">
                        {t('partners.roles.prospect')}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={domId('p-role-supplier')}
                        checked={roles.isSupplier}
                        onCheckedChange={(checked) =>
                          setValue('isSupplier', checked === true, { shouldDirty: true })
                        }
                      />
                      <Label htmlFor={domId('p-role-supplier')} className="font-normal">
                        {t('partners.roles.supplier')}
                      </Label>
                    </div>
                  </div>
                  {errors.isCustomer?.message === undefined ? null : (
                    <p className="text-xs text-destructive">{errors.isCustomer.message}</p>
                  )}
                </PartnerFormSection>

                <Separator />

                <section className="grid gap-4 sm:grid-cols-2">
                  <h3 className="text-sm font-semibold sm:col-span-2">
                    {t('partners.sections.masterData')}
                  </h3>

                  <FormField id={domId('p-type')} label={t('partners.fields.type')} required>
                    <Select
                      value={type ?? ''}
                      onValueChange={(value) =>
                        setValue('type', value as FormValues['type'], { shouldDirty: true })
                      }
                    >
                      <SelectTrigger id={domId('p-type')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTNER_TYPES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`partners.types.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <FormField id={domId('p-status')} label={t('partners.fields.status')} required>
                    <Select
                      value={form.watch('status') ?? 'ACTIVE'}
                      onValueChange={(value) =>
                        setValue('status', value as NonNullable<FormValues['status']>, {
                          shouldDirty: true,
                        })
                      }
                    >
                      <SelectTrigger id={domId('p-status')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTNER_STATUSES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`partners.statuses.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <FormField
                    id={domId('p-industry')}
                    label={t('conditions.categoryField')}
                    error={errors.industry?.message}
                  >
                    <CustomerCategorySelect
                      id={domId('p-industry')}
                      value={form.watch('industry')}
                      onChange={(value) =>
                        setValue('industry', value, { shouldDirty: true, shouldValidate: true })
                      }
                    />
                  </FormField>

                  <FormField
                    id={domId('p-industry-code')}
                    label={t('partners.fields.industryCode')}
                    description={t('partners.fields.industryCodeHint')}
                    error={
                      errors.industryCode === undefined
                        ? undefined
                        : t('partners.fields.industryCodeInvalid', {
                            max: CUSTOMER_INDUSTRY_CODE_MAX_LENGTH,
                          })
                    }
                  >
                    <div className="flex items-center gap-1">
                      <IndustryCodeSelect
                        countryCode={form.watch('countryCode')}
                        value={form.watch('industryCode')}
                        invalid={errors.industryCode !== undefined}
                        onChange={(code, industry) => {
                          setValue('industryCode', code, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          if (industry) setValue('industry', industry, { shouldDirty: true });
                        }}
                      />
                      <InlineHelp
                        articleId="partners.customer-file"
                        anchor="industry-code"
                        label={t('partners.fields.industryCodeHint')}
                      />
                    </div>
                  </FormField>

                  <FormField
                    id={domId('p-number')}
                    label={t('partners.fields.partnerNumber')}
                    description={t('partners.fields.partnerNumberHint')}
                    error={errors.partnerNumber?.message}
                  >
                    <Input
                      id={domId('p-number')}
                      {...form.register('partnerNumber', emptyToNull)}
                    />
                  </FormField>

                  <FormField
                    id={domId('p-country')}
                    label={t('partners.fields.countryCode')}
                    error={errors.countryCode?.message}
                  >
                    <CountrySelect
                      clearable={isEdit}
                      emptyValueLabel={t('legacy.unknown')}
                      id={domId('p-country')}
                      value={form.watch('countryCode')}
                      onChange={(value) => {
                        if (value !== form.getValues('countryCode')) {
                          const hadSubdivision =
                            form.getValues('subdivisionCode') || form.getValues('subdivisionName');
                          setValue('subdivisionCode', null, { shouldDirty: true });
                          setValue('subdivisionName', null, { shouldDirty: true });
                          if (hadSubdivision) toast.info(t('subdivision.cleared'));
                        }
                        setValue('countryCode', isEdit && value === '' ? null : value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </FormField>
                  {type === 'COMPANY' ? (
                    <>
                      <FormField
                        id={domId('p-company')}
                        label={t('partners.fields.companyName')}
                        required
                        error={errors.companyName?.message}
                      >
                        <Input
                          id={domId('p-company')}
                          {...form.register('companyName', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-trading')}
                        label={t('partners.fields.tradingName')}
                        error={errors.tradingName?.message}
                      >
                        <Input
                          id={domId('p-trading')}
                          {...form.register('tradingName', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-legal-form')}
                        label={t('partners.fields.legalForm')}
                        error={
                          errors.legalFormName?.message
                            ? t(errors.legalFormName.message)
                            : errors.legalFormCode?.message
                        }
                      >
                        <LegalFormSelect
                          id={domId('p-legal-form')}
                          country={form.watch('countryCode') ?? ''}
                          legacy={form.watch('legalForm')}
                          value={{
                            legalFormCode: form.watch('legalFormCode'),
                            legalFormName: form.watch('legalFormName'),
                            legalFormJurisdiction: form.watch('legalFormJurisdiction'),
                          }}
                          onChange={(value, replacesSelection) => {
                            if (replacesSelection)
                              setValue('legalForm', null, { shouldDirty: true });
                            for (const key of [
                              'legalFormCode',
                              'legalFormName',
                              'legalFormJurisdiction',
                            ] as const)
                              setValue(key, value[key], { shouldDirty: true });
                          }}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-department')}
                        label={t('partners.fields.department')}
                        error={errors.department?.message}
                      >
                        <Input
                          id={domId('p-department')}
                          {...form.register('department', emptyToNull)}
                        />
                      </FormField>
                    </>
                  ) : (
                    <>
                      <FormField
                        id={domId('p-first')}
                        label={t('partners.fields.firstName')}
                        required
                        error={errors.firstName?.message}
                      >
                        <Input id={domId('p-first')} {...form.register('firstName', emptyToNull)} />
                      </FormField>
                      <FormField
                        id={domId('p-last')}
                        label={t('partners.fields.lastName')}
                        required
                        error={errors.lastName?.message}
                      >
                        <Input id={domId('p-last')} {...form.register('lastName', emptyToNull)} />
                      </FormField>
                    </>
                  )}

                  <FormField
                    id={domId('p-email')}
                    label={t('partners.fields.email')}
                    error={errors.email?.message}
                  >
                    <Input
                      id={domId('p-email')}
                      type="email"
                      {...form.register('email', emptyToNull)}
                    />
                  </FormField>
                  <FormField
                    id={domId('p-phone')}
                    label={t('partners.fields.phone')}
                    error={errors.phone?.message}
                  >
                    <Input id={domId('p-phone')} {...form.register('phone', emptyToNull)} />
                  </FormField>
                  <FormField
                    id={domId('p-mobile')}
                    label={t('partners.fields.mobile')}
                    error={errors.mobile?.message}
                  >
                    <Input id={domId('p-mobile')} {...form.register('mobile', emptyToNull)} />
                  </FormField>
                  <FormField
                    id={domId('p-website')}
                    label={t('partners.fields.website')}
                    error={errors.website?.message}
                  >
                    <Input id={domId('p-website')} {...form.register('website', emptyToNull)} />
                  </FormField>
                </section>

                <Separator />

                <PartnerFormSection title={t('partners.sections.address')}>
                  <label className="col-span-full flex min-h-11 items-center gap-2 text-sm">
                    <input type="checkbox" {...form.register('geocodingPublic')} />
                    {t('partnerMap.publicAddress')}
                  </label>
                  <FormField id={domId('p-street')} label={t('partners.fields.street')}>
                    <Input id={domId('p-street')} {...form.register('street', emptyToNull)} />
                  </FormField>
                  <FormField id={domId('p-house')} label={t('partners.fields.houseNumber')}>
                    <Input id={domId('p-house')} {...form.register('houseNumber', emptyToNull)} />
                  </FormField>
                  <FormField id={domId('p-address2')} label={t('partners.fields.addressLine2')}>
                    <Input
                      id={domId('p-address2')}
                      {...form.register('addressLine2', emptyToNull)}
                    />
                  </FormField>
                  <FormField id={domId('p-postal')} label={t('partners.fields.postalCode')}>
                    <Input id={domId('p-postal')} {...form.register('postalCode', emptyToNull)} />
                  </FormField>
                  <FormField id={domId('p-city')} label={t('partners.fields.city')}>
                    <Input id={domId('p-city')} {...form.register('city', emptyToNull)} />
                  </FormField>
                  <FormField id={domId('p-region')} label={t('partners.fields.region')}>
                    <Input
                      id={domId('p-region')}
                      {...form.register('region', {
                        ...emptyToNull,
                        onChange: () => setValue('regionOrigin', 'MANUAL', { shouldDirty: true }),
                      })}
                    />
                  </FormField>

                  <SubdivisionSelect
                    id={domId('p-subdivision-1')}
                    value={{
                      countryCode: form.watch('countryCode'),
                      subdivisionCode: form.watch('subdivisionCode'),
                      subdivisionName: form.watch('subdivisionName'),
                    }}
                    city={form.watch('city')}
                    postalCode={form.watch('postalCode')}
                    region={form.watch('region')}
                    onChange={(value) => {
                      setValue('subdivisionCode', value.subdivisionCode, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue('subdivisionName', value.subdivisionName, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />

                  <div className="sm:col-span-2">
                    {(!isEdit ||
                      ['countryCode', 'postalCode', 'city', 'region'].some((field) =>
                        editedFieldsRef.current.has(field),
                      )) && (
                      <AddressResolutionAssist
                        countryCode={form.watch('countryCode')?.toUpperCase()}
                        postalCode={form.watch('postalCode')}
                        city={form.watch('city')}
                        region={form.watch('region')}
                        regionOrigin={form.watch('regionOrigin')}
                        addressDirty={Boolean(
                          form.formState.dirtyFields.countryCode ||
                          form.formState.dirtyFields.postalCode ||
                          form.formState.dirtyFields.city,
                        )}
                        onResolve={(change) => {
                          setValue('region', change.region, { shouldDirty: true });
                          setValue('regionOrigin', change.regionOrigin, { shouldDirty: true });
                          if (change.city !== undefined)
                            setValue('city', change.city, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                        }}
                        onSelect={(suggestion) => {
                          setValue(
                            'subdivisionCode',
                            suggestion.countryCode === 'FR' ? suggestion.districtCode : null,
                            { shouldDirty: true, shouldValidate: true },
                          );
                          setValue(
                            'subdivisionName',
                            suggestion.countryCode === 'FR' ? suggestion.district : null,
                            { shouldDirty: true, shouldValidate: true },
                          );

                          setValue('regionOrigin', 'MANUAL', { shouldDirty: true });
                          setValue('city', suggestion.city, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setValue('region', suggestion.region, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    )}
                  </div>
                </PartnerFormSection>

                <Separator />

                <PartnerFormSection title={t('partners.sections.tax')}>
                  <FormField
                    id={domId('p-vat')}
                    label={t('partners.fields.vatId')}
                    error={errors.vatId?.message}
                  >
                    <Input id={domId('p-vat')} {...form.register('vatId', emptyToNull)} />
                  </FormField>
                  <FormField
                    id={domId('p-tax-number')}
                    label={t('partners.fields.taxNumber')}
                    error={errors.taxNumber?.message}
                  >
                    <Input
                      id={domId('p-tax-number')}
                      {...form.register('taxNumber', emptyToNull)}
                    />
                  </FormField>
                  {frenchIdentifierFields('p')}
                  <FormField id={domId('p-tax-category')} label={t('partners.fields.taxCategory')}>
                    <Select
                      value={form.watch('taxCategory') ?? NONE}
                      onValueChange={(value) =>
                        setValue(
                          'taxCategory',
                          value === NONE ? null : (value as NonNullable<FormValues['taxCategory']>),
                          {
                            shouldDirty: true,
                          },
                        )
                      }
                    >
                      <SelectTrigger id={domId('p-tax-category')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isEdit && <SelectItem value={NONE}>{t('legacy.unknown')}</SelectItem>}
                        {TAX_CATEGORIES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`partners.taxCategories.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField
                    id={domId('p-gln')}
                    label={t('partners.fields.gln')}
                    error={errors.gln?.message}
                  >
                    <Input id={domId('p-gln')} {...form.register('gln', emptyToNull)} />
                  </FormField>
                  <FormField
                    id={domId('p-eori')}
                    label={t('partners.fields.eoriNumber')}
                    error={errors.eoriNumber?.message}
                  >
                    <Input id={domId('p-eori')} {...form.register('eoriNumber', emptyToNull)} />
                  </FormField>
                  <FormField
                    id={domId('p-currency')}
                    label={t('partners.fields.currency')}
                    error={errors.currency?.message}
                  >
                    <Input
                      id={domId('p-currency')}
                      maxLength={3}
                      {...form.register('currency', emptyToUndefined)}
                    />
                  </FormField>
                  <FormField
                    id={domId('p-language')}
                    label={t('partners.fields.preferredLanguage')}
                    description={t('partners.fields.preferredLanguageHint')}
                    error={errors.preferredLanguage?.message}
                  >
                    <div className="flex items-center gap-1">
                      <Select
                        value={form.watch('preferredLanguage') ?? NONE}
                        onValueChange={(value) =>
                          setValue(
                            'preferredLanguage',
                            value === NONE ? null : (value as FormValues['preferredLanguage']),
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          )
                        }
                      >
                        <SelectTrigger id={domId('p-language')} className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {isEdit && <SelectItem value={NONE}>{t('legacy.unknown')}</SelectItem>}
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem key={language} value={language}>
                              {t(`documents.languages.${language}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InlineHelp
                        articleId="partners.customer-file"
                        anchor="document-language"
                        label={t('partners.fields.preferredLanguageHint')}
                      />
                    </div>
                  </FormField>
                </PartnerFormSection>

                {roles.isCustomer ? (
                  <>
                    <Separator />
                    <PartnerFormSection title={t('partners.sections.customerConditions')}>
                      <FormField
                        id={domId('p-debtor')}
                        label={t('partners.fields.debtorAccountNumber')}
                        description={t('partners.fields.debtorAccountHint')}
                        error={errors.debtorAccountNumber?.message}
                      >
                        <Input
                          id={domId('p-debtor')}
                          {...form.register('debtorAccountNumber', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-credit-limit')}
                        label={t('partners.fields.creditLimit')}
                        error={errors.creditLimit?.message}
                      >
                        <Input
                          id={domId('p-credit-limit')}
                          {...form.register('creditLimit', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-credit-rating')}
                        label={t('partners.fields.creditRating')}
                        description={t('partners.fields.creditRatingHint')}
                        error={errors.creditRating?.message}
                      >
                        <Input
                          id={domId('p-credit-rating')}
                          type="number"
                          {...form.register('creditRating', numberOrNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-price-group')}
                        label={t('partners.fields.priceGroup')}
                        error={errors.priceGroupId?.message ?? errors.priceGroup?.message}
                        description={
                          !canEditPricing ? t('permissions.customerPricingReadOnly') : undefined
                        }
                      >
                        <PriceGroupSelect
                          id={domId('p-price-group')}
                          disabled={!canEditPricing}
                          value={form.watch('priceGroupId')}
                          onChange={(value) =>
                            setValue('priceGroupId', value, { shouldDirty: true })
                          }
                        />
                      </FormField>
                      <FormField
                        id={domId('p-payment-term')}
                        label={t('partners.fields.paymentTerms')}
                        className="sm:col-span-2"
                      >
                        <PaymentTermSelect
                          id={domId('p-payment-term')}
                          disabled={!canEditPricing}
                          value={paymentTermId}
                          customLabel={customPaymentLabel}
                          onChange={(term) => {
                            setCustomPaymentOpen(false);
                            setValue('paymentTermId', term.id, { shouldDirty: true });
                            setValue('paymentTermsDays', term.dueDays, {
                              shouldDirty: true,
                            });
                            setValue('discountPercent', term.cashDiscountPct, {
                              shouldDirty: true,
                            });
                            setValue('discountDays', term.cashDiscountDays, {
                              shouldDirty: true,
                            });
                          }}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t(
                            hasSelectedPaymentTerm
                              ? 'paymentTerms.selectionHint'
                              : 'paymentTerms.customHint',
                          )}
                        </p>
                        <InlineHelp
                          articleId="partners.customer-file"
                          anchor="payment-terms"
                          label={t('partners.fields.paymentTerms')}
                        />
                        {inconsistentPaymentValues ? (
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            {t('paymentTerms.inconsistentValues')}
                          </p>
                        ) : null}
                        {hasSelectedPaymentTerm ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setValue('paymentTermId', null, { shouldDirty: true });
                              setCustomPaymentOpen(true);
                              setFocusField('paymentTermsDays');
                            }}
                          >
                            {t('paymentTerms.customize')}
                          </Button>
                        ) : (
                          <details
                            className="group rounded-md border border-border p-3"
                            open={customPaymentOpen}
                            onToggle={(event) => setCustomPaymentOpen(event.currentTarget.open)}
                          >
                            <summary className="flex min-h-9 cursor-pointer list-none items-center justify-between text-sm font-medium">
                              {t('paymentTerms.customAgreement')}
                              <ChevronDown
                                className="h-4 w-4 transition-transform group-open:rotate-180"
                                aria-hidden="true"
                              />
                            </summary>
                            <div className="grid gap-4 pt-3 sm:grid-cols-3">
                              <FormField
                                id={domId('p-payment-days')}
                                label={t('partners.fields.paymentTermsDays')}
                                error={errors.paymentTermsDays?.message}
                              >
                                <Input
                                  id={domId('p-payment-days')}
                                  type="number"
                                  {...form.register(
                                    'paymentTermsDays',
                                    isEdit ? numberOrNull : numberOrUndefined,
                                  )}
                                  onChange={(event) => {
                                    form
                                      .register(
                                        'paymentTermsDays',
                                        isEdit ? numberOrNull : numberOrUndefined,
                                      )
                                      .onChange(event);
                                    setValue('paymentTermId', null, { shouldDirty: true });
                                  }}
                                />
                              </FormField>
                              <FormField
                                id={domId('p-discount-pct')}
                                label={t('partners.fields.discountPercent')}
                                error={errors.discountPercent?.message}
                              >
                                <Input
                                  id={domId('p-discount-pct')}
                                  disabled={!canEditPricing}
                                  {...form.register(
                                    'discountPercent',
                                    isEdit ? emptyToNull : emptyToUndefined,
                                  )}
                                />
                              </FormField>
                              <FormField
                                id={domId('p-discount-days')}
                                label={t('partners.fields.discountDays')}
                                error={errors.discountDays?.message}
                              >
                                <Input
                                  id={domId('p-discount-days')}
                                  disabled={!canEditPricing}
                                  type="number"
                                  {...form.register(
                                    'discountDays',
                                    isEdit ? numberOrNull : numberOrUndefined,
                                  )}
                                />
                              </FormField>
                            </div>
                          </details>
                        )}
                      </FormField>
                      <FormField
                        id={domId('p-default-discount')}
                        label={t('partners.fields.defaultDiscountPct')}
                        error={errors.defaultDiscountPct?.message}
                      >
                        <Input
                          id={domId('p-default-discount')}
                          disabled={!canEditPricing}
                          {...form.register(
                            'defaultDiscountPct',
                            isEdit ? emptyToNull : emptyToUndefined,
                          )}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-delivery-terms')}
                        label={t('partners.fields.deliveryTerms')}
                        error={errors.deliveryTerms?.message}
                      >
                        <DeliveryTermSelect
                          id={domId('p-delivery-terms')}
                          value={form.watch('deliveryTerms')}
                          onChange={(value) =>
                            setValue('deliveryTerms', value, {
                              shouldDirty: true,
                              shouldValidate: true,
                            })
                          }
                        />
                      </FormField>
                      <FormField
                        id={domId('p-shelf-life')}
                        label={t('partners.fields.minRemainingShelfLifeDays')}
                        error={errors.minRemainingShelfLifeDays?.message}
                      >
                        <Input
                          id={domId('p-shelf-life')}
                          type="number"
                          {...form.register('minRemainingShelfLifeDays', numberOrNull)}
                        />
                      </FormField>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={domId('p-delivery-block')}
                          checked={form.watch('deliveryBlock') === true}
                          onCheckedChange={(checked) =>
                            setValue('deliveryBlock', checked === true, {
                              shouldDirty: true,
                            })
                          }
                        />
                        <Label htmlFor={domId('p-delivery-block')} className="font-normal">
                          {t('partners.fields.deliveryBlock')}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={domId('p-dunning-block')}
                          checked={form.watch('dunningBlock') === true}
                          onCheckedChange={(checked) =>
                            setValue('dunningBlock', checked === true, { shouldDirty: true })
                          }
                        />
                        <Label htmlFor={domId('p-dunning-block')} className="font-normal">
                          {t('partners.fields.dunningBlock')}
                        </Label>
                      </div>
                    </PartnerFormSection>
                  </>
                ) : null}

                {roles.isProspect ? (
                  <>
                    <Separator />
                    <PartnerFormSection title={t('partners.sections.prospect')}>
                      <FormField
                        id={domId('p-lead-status')}
                        label={t('partners.fields.leadStatus')}
                      >
                        <Select
                          value={form.watch('leadStatus') ?? 'NEW'}
                          onValueChange={(value) =>
                            setValue('leadStatus', value as FormValues['leadStatus'], {
                              shouldDirty: true,
                            })
                          }
                        >
                          <SelectTrigger id={domId('p-lead-status')}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((value) => (
                              <SelectItem key={value} value={value}>
                                {t(`partners.leadStatuses.${value}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField
                        id={domId('p-lead-source')}
                        label={t('partners.fields.leadSource')}
                      >
                        <Select
                          value={form.watch('leadSource') ?? NONE}
                          onValueChange={(value) =>
                            setValue(
                              'leadSource',
                              value === NONE ? null : (value as FormValues['leadSource']),
                              { shouldDirty: true },
                            )
                          }
                        >
                          <SelectTrigger id={domId('p-lead-source')}>
                            <SelectValue placeholder={t('common.pleaseSelect')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>{t('common.none')}</SelectItem>
                            {LEAD_SOURCES.map((value) => (
                              <SelectItem key={value} value={value}>
                                {t(`partners.leadSources.${value}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField
                        id={domId('p-lead-score')}
                        label={t('partners.fields.leadScore')}
                        error={errors.leadScore?.message}
                      >
                        <Input
                          id={domId('p-lead-score')}
                          type="number"
                          min={0}
                          max={100}
                          {...form.register('leadScore', numberOrNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-deal-size')}
                        label={t('partners.fields.dealSizeEstimated')}
                        error={errors.dealSizeEstimated?.message}
                      >
                        <Input
                          id={domId('p-deal-size')}
                          {...form.register('dealSizeEstimated', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-win-probability')}
                        label={t('partners.fields.winProbability')}
                        error={errors.winProbability?.message}
                      >
                        <Input
                          id={domId('p-win-probability')}
                          type="number"
                          min={0}
                          max={100}
                          {...form.register('winProbability', numberOrNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-closing-date')}
                        label={t('partners.fields.expectedClosingDate')}
                        error={errors.expectedClosingDate?.message}
                      >
                        <Input
                          id={domId('p-closing-date')}
                          type="date"
                          {...form.register('expectedClosingDate', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-lost-reason')}
                        label={t('partners.fields.lostReason')}
                        error={errors.lostReason?.message}
                        className="sm:col-span-2"
                      >
                        <Input
                          id={domId('p-lost-reason')}
                          {...form.register('lostReason', emptyToNull)}
                        />
                      </FormField>
                    </PartnerFormSection>
                  </>
                ) : null}

                {roles.isSupplier ? (
                  <>
                    <Separator />
                    <PartnerFormSection title={t('partners.sections.supplier')}>
                      <FormField
                        id={domId('p-creditor')}
                        label={t('partners.fields.creditorAccountNumber')}
                        description={t('partners.fields.creditorAccountHint')}
                        error={errors.creditorAccountNumber?.message}
                      >
                        <div className="flex items-center gap-1">
                          <Input
                            id={domId('p-creditor')}
                            {...form.register('creditorAccountNumber', emptyToNull)}
                          />
                          <InlineHelp
                            articleId="accounting.creditor-accounts"
                            anchor="meaning"
                            label={t('partners.fields.creditorAccountHint')}
                          />
                        </div>
                      </FormField>
                      <FormField
                        id={domId('p-supplier-rating')}
                        label={t('partners.fields.supplierRating')}
                      >
                        <Select
                          value={form.watch('supplierRating') ?? NONE}
                          onValueChange={(value) =>
                            setValue(
                              'supplierRating',
                              value === NONE ? null : (value as FormValues['supplierRating']),
                              { shouldDirty: true },
                            )
                          }
                        >
                          <SelectTrigger id={domId('p-supplier-rating')}>
                            <SelectValue placeholder={t('common.pleaseSelect')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>{t('common.none')}</SelectItem>
                            {SUPPLIER_RATINGS.map((value) => (
                              <SelectItem key={value} value={value}>
                                {t(`partners.supplierRatings.${value}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField
                        id={domId('p-min-order')}
                        label={t('partners.fields.minimumOrderValue')}
                        error={errors.minimumOrderValue?.message}
                      >
                        <Input
                          id={domId('p-min-order')}
                          {...form.register('minimumOrderValue', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-delivery-time')}
                        label={t('partners.fields.deliveryTimeDays')}
                        error={errors.deliveryTimeDays?.message}
                      >
                        <Input
                          id={domId('p-delivery-time')}
                          type="number"
                          {...form.register('deliveryTimeDays', numberOrNull)}
                        />
                      </FormField>
                      <FormField id={domId('p-incoterms')} label={t('partners.fields.incoterms')}>
                        <Select
                          value={form.watch('incoterms') ?? NONE}
                          onValueChange={(value) =>
                            setValue(
                              'incoterms',
                              value === NONE ? null : (value as FormValues['incoterms']),
                              { shouldDirty: true },
                            )
                          }
                        >
                          <SelectTrigger id={domId('p-incoterms')}>
                            <SelectValue placeholder={t('common.pleaseSelect')} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value={NONE}>{t('common.none')}</SelectItem>
                            {INCOTERMS.map((value) => (
                              <SelectItem key={value} value={value}>
                                {value} — {t(`partners.incoterms.${value}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField
                        id={domId('p-iban')}
                        label={t('partners.fields.supplierIban')}
                        error={errors.supplierIban?.message}
                      >
                        <Input
                          id={domId('p-iban')}
                          {...form.register('supplierIban', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-bic')}
                        label={t('partners.fields.supplierBic')}
                        error={errors.supplierBic?.message}
                      >
                        <Input id={domId('p-bic')} {...form.register('supplierBic', emptyToNull)} />
                      </FormField>
                      <FormField
                        id={domId('p-bank')}
                        label={t('partners.fields.supplierBankName')}
                        error={errors.supplierBankName?.message}
                      >
                        <Input
                          id={domId('p-bank')}
                          {...form.register('supplierBankName', emptyToNull)}
                        />
                      </FormField>
                    </PartnerFormSection>
                  </>
                ) : null}

                <Separator />

                <section className="grid gap-4">
                  <FormField
                    id={domId('p-tags')}
                    label={t('partners.fields.tags')}
                    description={t('partners.fields.tagsHint')}
                    error={errors.tags?.message}
                  >
                    <Input
                      id={domId('p-tags')}
                      defaultValue={tags.join(', ')}
                      onChange={(event) =>
                        setValue(
                          'tags',
                          event.target.value
                            .split(',')
                            .map((entry) => entry.trim())
                            .filter((entry) => entry.length > 0),
                          { shouldDirty: true },
                        )
                      }
                    />
                  </FormField>
                </section>

                <CustomFieldLoadState
                  pending={definitions.isPending}
                  error={definitions.isError}
                  retry={() => void definitions.refetch()}
                />
                {visibleFields.length === 0 ? null : (
                  <>
                    <Separator />
                    <CategorizedCustomFields
                      recordAvailability={definitions.data?.availability}
                      definitions={visibleFields}
                      values={customFields}
                      mode="edit"
                      errors={customFieldErrors}
                      idPrefix={domId('partner-form-custom-field')}
                      className="w-full"
                      onChange={(key, value) =>
                        setValue(
                          'customFields',
                          { ...form.getValues('customFields'), [key]: value },
                          { shouldDirty: true },
                        )
                      }
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {preset === 'all' ? (
                  <section className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold">{t('partners.quick.step1')}</h3>
                    <div className="flex flex-wrap gap-5">
                      {(
                        [
                          ['isCustomer', 'customer'],
                          ['isProspect', 'prospect'],
                          ['isSupplier', 'supplier'],
                        ] as const
                      ).map(([field, label]) => (
                        <div key={field} className="flex items-center gap-2">
                          <Checkbox
                            id={`p-quick-role-${label}`}
                            checked={form.watch(field) === true}
                            onCheckedChange={(checked) =>
                              setValue(field, checked === true, { shouldDirty: true })
                            }
                          />
                          <Label htmlFor={`p-quick-role-${label}`} className="font-normal">
                            {t(`partners.roles.${label}`)}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {errors.isCustomer?.message === undefined ? null : (
                      <p className="text-xs text-destructive">{errors.isCustomer.message}</p>
                    )}
                  </section>
                ) : null}

                <section className="grid gap-4 sm:grid-cols-2">
                  <FormField id={domId('p-quick-type')} label={t('partners.fields.type')} required>
                    <Select
                      value={type ?? ''}
                      onValueChange={(value) =>
                        setValue('type', value as FormValues['type'], { shouldDirty: true })
                      }
                    >
                      <SelectTrigger id={domId('p-quick-type')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTNER_TYPES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`partners.types.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField id={domId('p-quick-country')} label={t('partners.fields.countryCode')}>
                    <CountrySelect
                      clearable={isEdit}
                      emptyValueLabel={t('legacy.unknown')}
                      id={domId('p-quick-country')}
                      value={form.watch('countryCode')}
                      onChange={(value) => {
                        if (value !== form.getValues('countryCode')) {
                          const hadSubdivision =
                            form.getValues('subdivisionCode') || form.getValues('subdivisionName');
                          setValue('subdivisionCode', null, { shouldDirty: true });
                          setValue('subdivisionName', null, { shouldDirty: true });
                          if (hadSubdivision) toast.info(t('subdivision.cleared'));
                        }
                        setValue('countryCode', isEdit && value === '' ? null : value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </FormField>
                  {type === 'COMPANY' ? (
                    <FormField
                      id={domId('p-quick-legal-form')}
                      label={t('partners.fields.legalForm')}
                      error={
                        errors.legalFormName?.message
                          ? t(errors.legalFormName.message)
                          : errors.legalFormCode?.message
                      }
                    >
                      <LegalFormSelect
                        id={domId('p-quick-legal-form')}
                        country={form.watch('countryCode') ?? ''}
                        legacy={form.watch('legalForm')}
                        value={{
                          legalFormCode: form.watch('legalFormCode'),
                          legalFormName: form.watch('legalFormName'),
                          legalFormJurisdiction: form.watch('legalFormJurisdiction'),
                        }}
                        onChange={(value, replacesSelection) => {
                          if (replacesSelection) setValue('legalForm', null, { shouldDirty: true });
                          for (const key of [
                            'legalFormCode',
                            'legalFormName',
                            'legalFormJurisdiction',
                          ] as const)
                            setValue(key, value[key], { shouldDirty: true });
                        }}
                      />
                    </FormField>
                  ) : null}

                  {type === 'COMPANY' ? (
                    <FormField
                      id={domId('p-quick-company')}
                      label={t('partners.fields.companyName')}
                      required
                      error={errors.companyName?.message}
                    >
                      <Input
                        id={domId('p-quick-company')}
                        autoFocus
                        {...form.register('companyName', emptyToNull)}
                      />
                    </FormField>
                  ) : (
                    <>
                      <FormField
                        id={domId('p-quick-first')}
                        label={t('partners.fields.firstName')}
                        required
                        error={errors.firstName?.message}
                      >
                        <Input
                          id={domId('p-quick-first')}
                          {...form.register('firstName', emptyToNull)}
                        />
                      </FormField>
                      <FormField
                        id={domId('p-quick-last')}
                        label={t('partners.fields.lastName')}
                        required
                        error={errors.lastName?.message}
                      >
                        <Input
                          id={domId('p-quick-last')}
                          {...form.register('lastName', emptyToNull)}
                        />
                      </FormField>
                    </>
                  )}
                </section>

                <details className="group rounded-md border border-border p-4">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between font-medium">
                    {t('partners.quick.step2')}
                    <ChevronDown
                      className="h-4 w-4 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="grid gap-4 pt-4 sm:grid-cols-2">
                    <FormField id={domId('p-quick-postal')} label={t('partners.fields.postalCode')}>
                      <Input
                        id={domId('p-quick-postal')}
                        {...form.register('postalCode', emptyToNull)}
                      />
                    </FormField>
                    <FormField id={domId('p-quick-city')} label={t('partners.fields.city')}>
                      <Input id={domId('p-quick-city')} {...form.register('city', emptyToNull)} />
                    </FormField>
                    <FormField id={domId('p-quick-region')} label={t('partners.fields.region')}>
                      <Input
                        id={domId('p-quick-region')}
                        {...form.register('region', {
                          ...emptyToNull,
                          onChange: () => setValue('regionOrigin', 'MANUAL', { shouldDirty: true }),
                        })}
                      />
                    </FormField>
                    <SubdivisionSelect
                      id={domId('p-subdivision-2')}
                      value={{
                        countryCode: form.watch('countryCode'),
                        subdivisionCode: form.watch('subdivisionCode'),
                        subdivisionName: form.watch('subdivisionName'),
                      }}
                      city={form.watch('city')}
                      postalCode={form.watch('postalCode')}
                      region={form.watch('region')}
                      onChange={(value) => {
                        setValue('subdivisionCode', value.subdivisionCode, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setValue('subdivisionName', value.subdivisionName, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />

                    <div className="sm:col-span-2">
                      {(!isEdit ||
                        ['countryCode', 'postalCode', 'city', 'region'].some((field) =>
                          editedFieldsRef.current.has(field),
                        )) && (
                        <AddressResolutionAssist
                          countryCode={form.watch('countryCode')?.toUpperCase()}
                          postalCode={form.watch('postalCode')}
                          city={form.watch('city')}
                          region={form.watch('region')}
                          regionOrigin={form.watch('regionOrigin')}
                          addressDirty={Boolean(
                            form.formState.dirtyFields.countryCode ||
                            form.formState.dirtyFields.postalCode ||
                            form.formState.dirtyFields.city,
                          )}
                          onResolve={(change) => {
                            setValue('region', change.region, { shouldDirty: true });
                            setValue('regionOrigin', change.regionOrigin, {
                              shouldDirty: true,
                            });
                            if (change.city !== undefined)
                              setValue('city', change.city, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                          }}
                          onSelect={(suggestion) => {
                            setValue(
                              'subdivisionCode',
                              suggestion.countryCode === 'FR' ? suggestion.districtCode : null,
                              { shouldDirty: true, shouldValidate: true },
                            );
                            setValue(
                              'subdivisionName',
                              suggestion.countryCode === 'FR' ? suggestion.district : null,
                              { shouldDirty: true, shouldValidate: true },
                            );

                            setValue('regionOrigin', 'MANUAL', { shouldDirty: true });
                            setValue('city', suggestion.city, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                            setValue('region', suggestion.region, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                        />
                      )}
                    </div>
                    <label className="col-span-full flex min-h-11 items-center gap-2 text-sm">
                      <input type="checkbox" {...form.register('geocodingPublic')} />
                      {t('partnerMap.publicAddress')}
                    </label>
                    <FormField id={domId('p-quick-street')} label={t('partners.fields.street')}>
                      <Input
                        id={domId('p-quick-street')}
                        {...form.register('street', emptyToNull)}
                      />
                    </FormField>
                    <FormField id={domId('p-quick-house')} label={t('partners.fields.houseNumber')}>
                      <Input
                        id={domId('p-quick-house')}
                        {...form.register('houseNumber', emptyToNull)}
                      />
                    </FormField>
                  </div>
                </details>

                <details className="group rounded-md border border-border p-4">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between font-medium">
                    {t('partners.quick.step3')}
                    <ChevronDown
                      className="h-4 w-4 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="grid gap-4 pt-4 sm:grid-cols-2">
                    <FormField
                      id={domId('p-quick-email')}
                      label={t('partners.fields.email')}
                      error={errors.email?.message}
                    >
                      <Input
                        id={domId('p-quick-email')}
                        type="email"
                        {...form.register('email', emptyToNull)}
                      />
                    </FormField>
                    <FormField id={domId('p-quick-phone')} label={t('partners.fields.phone')}>
                      <Input id={domId('p-quick-phone')} {...form.register('phone', emptyToNull)} />
                    </FormField>
                  </div>
                </details>

                <details className="group rounded-md border border-border p-4">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between font-medium">
                    {t('partners.quick.step4')}
                    <ChevronDown
                      className="h-4 w-4 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="grid gap-4 pt-4 sm:grid-cols-2">
                    <FormField
                      id={domId('p-quick-vat')}
                      label={t('partners.fields.vatId')}
                      error={errors.vatId?.message}
                    >
                      <Input id={domId('p-quick-vat')} {...form.register('vatId', emptyToNull)} />
                    </FormField>
                    {frenchIdentifierFields('p-quick')}
                    <FormField
                      id={domId('p-quick-language')}
                      label={t('partners.fields.preferredLanguage')}
                      description={t('partners.fields.preferredLanguageHint')}
                      error={errors.preferredLanguage?.message}
                    >
                      <div className="flex items-center gap-1">
                        <Select
                          value={form.watch('preferredLanguage') ?? NONE}
                          onValueChange={(value) =>
                            setValue(
                              'preferredLanguage',
                              value === NONE ? null : (value as FormValues['preferredLanguage']),
                              { shouldDirty: true, shouldValidate: true },
                            )
                          }
                        >
                          <SelectTrigger id={domId('p-quick-language')} className="flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {isEdit && <SelectItem value={NONE}>{t('legacy.unknown')}</SelectItem>}
                            {DOCUMENT_LANGUAGES.map((language) => (
                              <SelectItem key={language} value={language}>
                                {t(`documents.languages.${language}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <InlineHelp
                          articleId="partners.customer-file"
                          anchor="document-language"
                          label={t('partners.fields.preferredLanguageHint')}
                        />
                      </div>
                    </FormField>
                    <FormField id={domId('p-quick-currency')} label={t('partners.fields.currency')}>
                      <Input
                        id={domId('p-quick-currency')}
                        maxLength={3}
                        {...form.register('currency', emptyToUndefined)}
                      />
                    </FormField>
                    {roles.isCustomer ? (
                      <FormField
                        id={domId('p-quick-payment')}
                        label={t('partners.fields.paymentTerms')}
                        className="sm:col-span-2"
                      >
                        <PaymentTermSelect
                          id={domId('p-quick-payment')}
                          disabled={!canEditPricing}
                          value={paymentTermId}
                          customLabel={customPaymentLabel}
                          onChange={(term) => {
                            setValue('paymentTermId', term.id, { shouldDirty: true });
                            setValue('paymentTermsDays', term.dueDays, {
                              shouldDirty: true,
                            });
                            setValue('discountPercent', term.cashDiscountPct, {
                              shouldDirty: true,
                            });
                            setValue('discountDays', term.cashDiscountDays, {
                              shouldDirty: true,
                            });
                          }}
                        />
                        <InlineHelp
                          articleId="partners.customer-file"
                          anchor="payment-terms"
                          label={t('partners.fields.paymentTerms')}
                        />
                      </FormField>
                    ) : null}
                  </div>
                </details>

                <p className="text-xs text-muted-foreground">
                  {t('partners.quick.completionHint')}
                </p>
              </>
            )}
          </div>
          <SheetFooter className="record-form-actions mt-0 shrink-0">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => requestOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="min-h-11" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('common.save')}
            </Button>
          </SheetFooter>
        </form>
      </RecordFormFrame>
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            keepEditing();
          }}
        >
          <AlertDialogTitle>{t('partners.discardTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('partners.discardDescription')}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={keepEditing}>{t('partners.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={discard}>{t('partners.discard')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
